import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  Calendar,
  Layers,
  Scissors,
  Sparkles,
  CreditCard,
  Lock,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison sur-mesure ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison sur-mesure
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Notre service de livraison de cartons sur-mesure {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que les axes de Vélizy sont denses et que votre temps est
            trop précieux pour être gâché dans les transports avec un
            véhicule encombré de cartons plats, notre logistique
            s&apos;occupe de tout.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Proximité et réactivité",
              body: "Nous desservons la totalité des quartiers de la ville (Le Mail, Louvois, Mozart, l'Europe…) ainsi que les communes voisines : Meudon, Clamart, Jouy-en-Josas, Bièvres et Versailles.",
              Icon: Truck,
            },
            {
              title: "Ponctualité millimétrée",
              body: "Nous fixons un rendez-vous fixe et rigoureusement respecté. Nos chauffeurs s'adaptent à vos contraintes horaires pour s'intégrer de manière fluide dans votre agenda.",
              Icon: Calendar,
            },
            {
              title: "Intégrité du matériel",
              body: "Vos fournitures sont acheminées à plat, scellées sous film étanche, propres, sèches et prêtes pour un montage immédiat à haute résistance.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Fournitures professionnelles ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Outils pros
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Fournitures professionnelles : les outils indispensables des
            déménageurs
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Un bon déménagement ne se résume pas aux cartons ; il nécessite
            un écosystème d&apos;accessoires techniques pour travailler à la
            fois vite et bien.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Adhésifs forte adhérence & dévidoirs",
              body: "Nos rubans adhésifs professionnels ne cèdent pas sous la charge ni avec les variations de température. Couplés à nos pistolets dévidoirs ergonomiques, ils vous permettent de sceller vos boîtes d'un seul geste ferme.",
              Icon: Scissors,
            },
            {
              title: "Film à bulles et papier de calage",
              body: "Indispensables pour isoler vos objets de décoration, verres ou appareils électroniques, ils absorbent l'intégralité des chocs et vibrations du camion.",
              Icon: Layers,
            },
            {
              title: "Housses de protection épaisses",
              body: "Conçues pour vos matelas, canapés et électroménagers, elles font écran contre la poussière, l'humidité et les frottements inévitables dans les cages d'escalier ou les monte-charges.",
              Icon: Sparkles,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-velizy-cta-emballage"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les accessoires d&apos;emballage
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Cartons renforcés ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Cartons renforcés
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Les cartons renforcés : le blindage de sécurité pour vos objets
            lourds
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Mettre des objets denses dans des cartons standards est le piège
            classique qui mène à la déchirure du fond. Pour vos charges les
            plus importantes, nos structures renforcées prennent le relais.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "La puissance de la double cannelure",
              body: "Dotés d'une double épaisseur de carton ondulé de qualité industrielle, nos cartons renforcés offrent une résistance mécanique supérieure. Ils supportent de lourdes charges sans se déformer ni s'affaisser.",
              Icon: ShieldCheck,
            },
            {
              title: "Idéal pour livres, archives et vaisselle",
              body: "Ce matériel est spécifiquement calibré pour vos livres, dossiers administratifs denses, outillages ou appareils culinaires compacts. De plus, leur structure ultra-rigide permet de les empiler au fond du camion en toute sécurité, servant de socle stable pour le reste du chargement.",
              Icon: Package,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Paiement sécurisé ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Paiement 100 % sécurisé
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Paiement en ligne sécurisé à 100 % : commandez en toute
              tranquillité
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              La sérénité de vos préparatifs commence dès l&apos;étape de
              votre commande sur notre boutique en ligne. Nous déployons les
              protocoles de sécurité les plus stricts pour vos transactions.
            </p>

            <div className="mt-6 space-y-4">
              {[
                {
                  title: "Chiffrement SSL avancé",
                  body: "Toutes vos données bancaires sont immédiatement cryptées lors de la validation du panier grâce au protocole SSL, garantissant qu'aucune information ne circule en clair sur le réseau.",
                  Icon: Lock,
                },
                {
                  title: "Dispositif 3D Secure",
                  body: "Chaque achat fait l'objet d'une double validation (via l'application de votre banque ou un code sécurisé par SMS) pour vous prémunir contre toute tentative d'utilisation frauduleuse de votre carte.",
                  Icon: ShieldCheck,
                },
                {
                  title: "Confidentialité totale",
                  body: "Vos coordonnées de paiement ne sont jamais enregistrées sur nos serveurs. La transaction est opérée directement via nos partenaires bancaires agréés pour un achat sans aucun risque.",
                  Icon: CreditCard,
                },
              ].map(({ title, body, Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-3 bg-white border border-[#cfdbe6] rounded-2xl p-4 md:p-5"
                >
                  <span className="flex items-center justify-center shrink-0 h-10 w-10 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 bg-white border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Lock className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                Vos données protégées
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Cryptage SSL, 3D Secure et zéro stockage : commandez vos
              cartons {city.aCity} en toute confiance, sans jamais
              compromettre la sécurité de votre carte bancaire.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Chiffrement SSL bout en bout
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Double validation 3D Secure
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Aucune donnée stockée chez nous
              </div>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Partenaires bancaires agréés
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 6 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-white border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#EEF3F7] border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-velizy-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
